/* ============================================================
   mt-ab.js — A/B testing harness for the MT home page.
   Loaded synchronously in <head> so variants are resolved before paint.

   ## How variant assignment is decided (in order)
   1. **URL parameter** — e.g. `?ab_cta_price_v1=with_price`. Used by ad
      campaigns to force a specific variant. Sets the cookie too so the
      variant sticks for follow-up visits.
      Also supported: `?ab=cta_price_v1:with_price,other_exp:variant_b`.
   2. **Cookie** — `mt_ab_<experiment_id>`. Persists 30 days. Once a visitor
      is assigned a variant, they keep seeing the same one.
   3. **Random pool** — first-time visitor with no URL/cookie. Picks a
      variant per `weights`. Sets the cookie. From then on, treats the
      visitor like #2 above.

   ## How variants apply to the page
   - The variant value is merged into `window.MT_TWEAKS` so other code can read it.
   - DOM mutations happen via the APPLIERS map below. Add an applier per tweak.
   - A class `mt-ab-<expid>-<variant>` is set on <html> for CSS-driven swaps
     (e.g. `html.mt-ab-cta_price_v1-with_price .mt-btn__price { display: inline }`).

   ## Analytics
   - GA4: pushes `{event: 'mt_ab_exposure', experiment_id, variant_id, assignment_source}`
     to `window.dataLayer`. Pick this up in GA4 via a custom event tag, or via
     Shopify's web pixel.
   - Microsoft Clarity: calls `clarity('set', 'mt_ab_<expid>', variant)` once Clarity loads.

   ## Adding a new experiment
   1. Add an entry to EXPERIMENTS below.
   2. If the tweak needs DOM mutation, add an applier in APPLIERS.
   3. (Optional) Add CSS that styles `html.mt-ab-<expid>-<variant>`.
   4. Push to Shopify.
   ============================================================ */
(function () {
  'use strict';

  // ============================================================
  // EXPERIMENT REGISTRY — sourced from assets/mt-ab-variants.js (loaded
  // BEFORE this script in <head>). Edit that file to add experiments,
  // change weights, or revise copy. Keeping data and logic in separate
  // files: mt-ab.js is stable infrastructure, mt-ab-variants.js churns
  // as themes/offers get tested.
  // ============================================================
  var EXPERIMENTS = (window && window.MT_AB_VARIANTS) ? window.MT_AB_VARIANTS : {};

  // ============================================================
  // Helpers
  // ============================================================
  var COOKIE_DAYS = 30;

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, days) {
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + ((days || COOKIE_DAYS) * 86400) + '; SameSite=Lax';
  }
  function pickWeighted(weights) {
    var total = 0, k;
    for (k in weights) total += weights[k];
    if (total <= 0) return Object.keys(weights)[0];
    var r = Math.random() * total;
    for (k in weights) { r -= weights[k]; if (r <= 0) return k; }
    return Object.keys(weights)[0];
  }

  // ============================================================
  // Resolve a single experiment to its variant assignment.
  // Returns { variant, source }.
  // ============================================================
  function resolve(expId, exp) {
    var params = new URLSearchParams(location.search);

    // 1. Per-experiment URL param: ?ab_<expid>=<variant>
    var urlVariant = params.get('ab_' + expId);
    if (urlVariant && exp.variants.hasOwnProperty(urlVariant)) {
      setCookie('mt_ab_' + expId, urlVariant);
      return { variant: urlVariant, source: 'url' };
    }

    // 2. Combined ?ab=<expid>:<variant>[,<expid>:<variant>] form
    var combined = params.get('ab');
    if (combined) {
      var pairs = combined.split(',');
      for (var i = 0; i < pairs.length; i++) {
        var parts = pairs[i].split(':');
        if (parts[0] === expId && parts[1] && exp.variants.hasOwnProperty(parts[1])) {
          setCookie('mt_ab_' + expId, parts[1]);
          return { variant: parts[1], source: 'url' };
        }
      }
    }

    // 3. Existing cookie
    var ck = getCookie('mt_ab_' + expId);
    if (ck && exp.variants.hasOwnProperty(ck)) {
      return { variant: ck, source: 'cookie' };
    }

    // 4. Random assignment from weighted pool
    var chosen = pickWeighted(exp.weights);
    setCookie('mt_ab_' + expId, chosen);
    return { variant: chosen, source: 'random' };
  }

  // ============================================================
  // Resolve all experiments. Run synchronously so CSS classes are set
  // BEFORE the body renders (no FOUC for class-driven variants).
  //
  // Two passes:
  //   1. Resolve every experiment to a variant + set its <html> class.
  //   2. Walk the resolved variants again and merge tweakOverrides, with
  //      full assignment context available — so a variant's `themeText`
  //      can look up the resolved theme_v1 variant and apply the right
  //      theme-keyed copy.
  //
  // Override key semantics:
  //   text         { keyA: '...', keyB: '...' }
  //                  → setTextContent on every [data-mt-text=key].
  //   images       { keyA: '/path.jpg' }
  //                  → set src / background-image on every [data-mt-image=key].
  //   themeText    { occult_belief: { keyA: '...' }, meta_therapy: { ... } }
  //                  → looks up assignments.theme_v1.variant, applies the
  //                    matching map. Used by headline_v1 alts whose copy
  //                    is theme-specific (so each (theme × alt) cell has
  //                    its own purpose-fit H1 / pre-CTA / etc.).
  //   themeImages  { occult_belief: { keyA: '/...' }, meta_therapy: {...} }
  //                  → same pattern, image swaps.
  //   text + themeText for the same key: themeText wins (it's the more
  //   specific override).
  //
  // text/images deep-merge across experiments (later experiments win on key
  // conflicts). Legacy flat tweaks (ctaPriceInline, etc.) shallow-overwrite.
  // ============================================================
  var assignments = {};
  var tweakOverrides = {};
  var hasOverrides = false;

  // Pass 1: resolve all experiments to variants
  for (var expId in EXPERIMENTS) {
    var exp = EXPERIMENTS[expId];
    if (!exp || !exp.variants) continue;
    var res = resolve(expId, exp);
    assignments[expId] = res;

    // <html> class used for CSS-driven variants
    if (document.documentElement) {
      document.documentElement.classList.add('mt-ab-' + expId + '-' + res.variant);
    }
  }

  // Pass 2: merge tweakOverrides, with full assignments visible (so themeText
  // / themeImages can resolve against the active theme_v1 variant).
  var activeThemeVariant = (assignments.theme_v1 || {}).variant || null;

  for (var expId2 in EXPERIMENTS) {
    var exp2 = EXPERIMENTS[expId2];
    if (!exp2 || !exp2.variants || !assignments[expId2]) continue;
    var variantTweaks = exp2.variants[assignments[expId2].variant] || {};

    for (var t in variantTweaks) {
      if (t === 'text' || t === 'images') {
        tweakOverrides[t] = tweakOverrides[t] || {};
        for (var k in variantTweaks[t]) {
          tweakOverrides[t][k] = variantTweaks[t][k];
          hasOverrides = true;
        }
      } else if (t === 'themeText' || t === 'themeImages') {
        // Theme-keyed: look up the right map based on the resolved theme_v1.
        // No-op if theme_v1 isn't running, resolved to default, or this alt
        // doesn't have copy for the active theme — preserves graceful
        // fallback to the theme's own base copy.
        if (!activeThemeVariant || !variantTweaks[t][activeThemeVariant]) continue;
        var targetKey = (t === 'themeText') ? 'text' : 'images';
        tweakOverrides[targetKey] = tweakOverrides[targetKey] || {};
        var themedMap = variantTweaks[t][activeThemeVariant];
        for (var kk in themedMap) {
          tweakOverrides[targetKey][kk] = themedMap[kk];
          hasOverrides = true;
        }
      } else {
        tweakOverrides[t] = variantTweaks[t];
        hasOverrides = true;
      }
    }
  }

  // Anti-FOUC: hide swap-target elements until apply() runs. Only added
  // when there's something to swap — organic / default-only traffic gets
  // no class added and no flash. CSS rule lives in mt-v2-head.liquid.
  if (hasOverrides && document.documentElement) {
    document.documentElement.classList.add('mt-ab-applying');
  }

  window.MT_AB_ASSIGNMENTS = assignments;
  window.MT_AB_OVERRIDES = tweakOverrides;

  // ============================================================
  // UTM capture for Clarity tagging.
  // UTMs land on the FIRST hit of a session (e.g. an ad click). We persist
  // them in sessionStorage so Clarity replays of deeper pages still get
  // tagged with the originating ad. GA4 already auto-attributes UTMs to
  // events, so we don't push them to the dataLayer.
  // ============================================================
  function captureUtms() {
    var params = new URLSearchParams(location.search);
    var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
    var found = {};
    var any = false;
    for (var i = 0; i < keys.length; i++) {
      var v = params.get(keys[i]);
      if (v) { found[keys[i]] = v; any = true; }
    }
    if (any) {
      try { sessionStorage.setItem('mt_utm', JSON.stringify(found)); } catch (e) {}
      return found;
    }
    try {
      var stored = sessionStorage.getItem('mt_utm');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return null;
  }
  var utms = captureUtms();

  // ============================================================
  // APPLIERS — run after DOM is parsed. Each function takes the resolved
  // value and mutates the page accordingly. Keep them idempotent.
  // ============================================================
  var APPLIERS = {
    // ----------------------------------------------------------
    // Text swap: {key: "new copy"} → set textContent on every
    // <* data-mt-text="key">. Idempotent. The CSS in mt-v2-head.liquid
    // hides these elements while mt-ab-applying is on <html>.
    // ----------------------------------------------------------
    text: function (textMap) {
      if (!textMap) return;
      for (var key in textMap) {
        // Escape double-quotes so the attribute selector is valid for any key.
        var sel = '[data-mt-text="' + key.replace(/"/g, '\\"') + '"]';
        var els = document.querySelectorAll(sel);
        for (var i = 0; i < els.length; i++) {
          els[i].textContent = textMap[key];
        }
      }
    },
    // ----------------------------------------------------------
    // Image swap: {key: "/path.jpg"} → set src (or background-image)
    // on every <* data-mt-image="key">. For <img>, also strips srcset
    // so the new src isn't out-bid by responsive variants.
    // ----------------------------------------------------------
    images: function (imageMap) {
      if (!imageMap) return;
      for (var key in imageMap) {
        var sel = '[data-mt-image="' + key.replace(/"/g, '\\"') + '"]';
        var els = document.querySelectorAll(sel);
        for (var i = 0; i < els.length; i++) {
          var el = els[i];
          if (el.tagName === 'IMG') {
            if (el.hasAttribute('srcset')) el.removeAttribute('srcset');
            el.src = imageMap[key];
          } else {
            el.style.backgroundImage = 'url("' + String(imageMap[key]).replace(/"/g, '\\"') + '")';
          }
        }
      }
    },
    // CTA price inline: add or remove " — $XX.XX" from button text.
    ctaPriceInline: function (showPrice) {
      var PRICES = {
        '52892476637551': '$54.99', // duo
        '52585688039791': '$29.99', // mt
        '52585687974255': '$29.99'  // gzt
      };
      document.querySelectorAll('[data-variant-id]').forEach(function (el) {
        if (el.matches('input, select, .mt-cart__row *, .mt-cart__upsell *')) return; // skip cart internals
        var vid = el.getAttribute('data-variant-id');
        var price = PRICES[vid];
        if (!price) return;
        var raw = (el.textContent || '').replace(/\s+/g, ' ').trim();
        // strip any existing " — $XX.XX"
        var stripped = raw.replace(/\s*—\s*\$\d+(\.\d+)?\s*/, ' ').replace(/\s+/g, ' ').trim();
        if (showPrice) {
          var arrowMatch = stripped.match(/^(.*?)([→↗➜])\s*$/);
          if (arrowMatch) {
            el.textContent = arrowMatch[1].trim() + ' — ' + price + ' ' + arrowMatch[2];
          } else {
            el.textContent = stripped + ' — ' + price;
          }
        } else {
          el.textContent = stripped;
        }
      });
    }
    // Add more appliers here as new experiments are added.
  };

  // ============================================================
  // Apply overrides + fire analytics
  // ============================================================
  function apply() {
    // Merge into MT_TWEAKS so other scripts read the variant value
    window.MT_TWEAKS = window.MT_TWEAKS || {};
    for (var k in tweakOverrides) window.MT_TWEAKS[k] = tweakOverrides[k];

    // Run appliers
    for (var t in tweakOverrides) {
      if (typeof APPLIERS[t] === 'function') {
        try { APPLIERS[t](tweakOverrides[t]); }
        catch (e) { console.error('[mt-ab] applier failed for ' + t, e); }
      }
    }

    // Anti-FOUC: reveal swap-target elements now that appliers ran.
    if (document.documentElement) {
      document.documentElement.classList.remove('mt-ab-applying');
    }

    fireAnalytics();
  }

  function fireAnalytics() {
    // GA4 / GTM dataLayer push (for any GTM container that's listening)
    // AND a direct gtag('event', ...) call (Shopify's auto-installed GA4
    // pixel forwards standard events but NOT custom dataLayer events,
    // so we have to call gtag directly to populate variant_id /
    // experiment_id custom dimensions in GA4 reports).
    window.dataLayer = window.dataLayer || [];
    function pushGa() {
      var sentAny = false;
      for (var expId in assignments) {
        window.dataLayer.push({
          event: 'mt_ab_exposure',
          experiment_id: expId,
          variant_id: assignments[expId].variant,
          assignment_source: assignments[expId].source
        });
        if (typeof window.gtag === 'function') {
          try {
            window.gtag('event', 'mt_ab_exposure', {
              experiment_id: expId,
              variant_id: assignments[expId].variant,
              assignment_source: assignments[expId].source
            });
            sentAny = true;
          } catch (e) {}
        }
      }
      return sentAny || typeof window.gtag !== 'function';
    }
    // gtag may load slightly after our script; retry briefly for the direct call.
    if (!pushGa()) {
      var gaTries = 0;
      var gaIv = setInterval(function () {
        if (typeof window.gtag === 'function') {
          for (var expId in assignments) {
            try {
              window.gtag('event', 'mt_ab_exposure', {
                experiment_id: expId,
                variant_id: assignments[expId].variant,
                assignment_source: assignments[expId].source
              });
            } catch (e) {}
          }
          clearInterval(gaIv);
        } else if (++gaTries > 40) {
          clearInterval(gaIv);
        }
      }, 250);
    }

    // Microsoft Clarity custom tags. Clarity can load late, so retry briefly.
    // Internal-traffic guard: if we're on the local preview proxy
    // (`http://127.0.0.1:9295` or localhost), tag the session and call
    // `clarity('stop')` so the recording isn't uploaded — Scott's editing
    // sessions were polluting the production Clarity dataset (CA1).
    function isInternalPreview() {
      try {
        var h = location.hostname;
        return h === '127.0.0.1' || h === 'localhost' || location.port === '9295';
      } catch (e) { return false; }
    }
    function tagClarity() {
      if (typeof window.clarity !== 'function') return false;
      if (isInternalPreview()) {
        try { window.clarity('set', 'mt_internal', 'preview'); } catch (e) {}
        try { window.clarity('stop'); } catch (e) {}
        return true; // signal "done" so we don't keep retrying
      }
      for (var expId in assignments) {
        try { window.clarity('set', 'mt_ab_' + expId, assignments[expId].variant); }
        catch (e) {}
      }
      if (utms) {
        for (var k in utms) {
          try { window.clarity('set', k, utms[k]); }
          catch (e) {}
        }
      }
      return true;
    }
    if (!tagClarity()) {
      var tries = 0;
      var iv = setInterval(function () {
        if (tagClarity() || ++tries > 40) clearInterval(iv);
      }, 250);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }

  // Public surface for debugging / mt-home.js
  window.MT_AB = {
    EXPERIMENTS: EXPERIMENTS,
    assignments: assignments,
    overrides: tweakOverrides,
    utms: utms,
    // Helper to clear cookie state — `MT_AB.reset()` from devtools
    reset: function () {
      for (var expId in EXPERIMENTS) {
        document.cookie = 'mt_ab_' + expId + '=; path=/; max-age=0';
      }
      try { sessionStorage.removeItem('mt_utm'); } catch (e) {}
      console.log('[mt-ab] all experiment cookies + UTM session cleared. Reload to re-randomize.');
    }
  };
})();
